import { Button, Typography } from "antd";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { connect } from "react-redux";
import OtpInput from "react18-input-otp";
import { translation } from "../../../../i18n/translationConstant";
import { UserRespose } from "../../../../redux/Reducer/userReducer";
import "./WebviewOTPInputComponent.css";
import { useLocation } from "react-router-dom";

interface IOTPInputProps {
  isOpen: boolean;
  onOtpSubmit: (otp: string) => void;
  onResend?: () => void;
  numInputs?: number;
  okText: string;
  title: string;
  user: UserRespose;
  mobileNumber?: string;
}

interface LocationState {
  redirect?: {
    fiuName?: string;
    fiuID?: string;
  };
}

const WebviewOTPInputComponent: React.FC<IOTPInputProps> = ({
  isOpen,
  okText,
  numInputs = 4,
  onOtpSubmit,
  onResend,
  title,
  user,
  mobileNumber,
}) => {
  const { t } = useTranslation();
  const otpResendDuration = 60;

  const [OTP, setOTP] = useState("");
  const [counter, setCounter] = useState(otpResendDuration);
  const timer = useRef<NodeJS.Timeout | null>(null);

  const location = useLocation<LocationState>();
  const redirect = location.state?.redirect;

  useEffect(() => {
    if (!isOpen) {
      setCounter(0);
      if (timer.current) clearTimeout(timer.current);
    } else {
      setCounter(otpResendDuration);
    }
    return () => {
      if (timer.current) clearTimeout(timer.current);
    };
  }, [isOpen]);

  useEffect(() => {
    if (counter <= 0) {
      if (timer.current) clearTimeout(timer.current);
      return;
    }
    timer.current = setTimeout(() => setCounter(counter - 1), 1000);
    return () => {
      if (timer.current) clearTimeout(timer.current);
    };
  }, [counter]);

  const changeInput = (otp: string) => setOTP(otp);

  const resendOTP = () => {
    setCounter(otpResendDuration);
    setOTP(""); // reset OTP input
    onResend?.();
  };

  if (!isOpen) return null;

  return (
    <div className="otpModalContainer">
      <Typography.Title level={2} className="otpTitle">
        {redirect?.fiuName || "FIU"} wants to securely access your financial
        information through Ink AA
      </Typography.Title>

      <Typography.Title level={3} className="otpSubtitle">
        {title || t(translation.lblEnterOTP)}
      </Typography.Title>

      <Typography.Text className="otpSentText">
        {t(translation.lblOtpSent)}{" "}
        <span className="otpNumber">{mobileNumber || user.mobileNumber}</span>
      </Typography.Text>

      <OtpInput
        value={OTP}
        onChange={changeInput}
        numInputs={numInputs}
        shouldAutoFocus
        isInputSecure
        containerStyle={{ display: "flex", justifyContent: "center", gap: "10px" }}
        inputStyle={{
          width: "60px",
          height: "60px",
          fontSize: "24px",
          textAlign: "center",
          border: "2px solid #d9d9d9",
          borderRadius: "10px",
          boxSizing: "border-box",
        }}
        inputProps={{
          onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => {
            if (e.key === "Enter" && OTP.length === numInputs) onOtpSubmit(OTP);
            if (e.key === "Backspace") {
              e.preventDefault();
              setOTP((prev) => prev.slice(0, prev.length - 1));
            }
          },
        }}
      />

      <div className="otpFooter">
        <Typography.Text className="counter">
          {counter > 0 ? `${counter} sec left` : ""}
        </Typography.Text>
        <Button type="link" disabled={counter > 0} onClick={resendOTP} className="resendButton">
          Resend OTP
        </Button>
      </div>

      <Button type="primary" block onClick={() => onOtpSubmit(OTP)} className="submitButton">
        {okText}
      </Button>
    </div>
  );
};

const mapStateToProps = (state: any) => ({
  user: state.user.user,
});

export default connect(mapStateToProps)(WebviewOTPInputComponent);
